import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type {
  ActualWindowRow,
  ActualWindows,
  YieldHistory,
  AddBinResult,
  HarvestLoad,
  LoadInput,
  Packout,
  PackoutGrade,
  PackoutGroupKey,
  PackoutInput,
  PackoutReport,
  Bin,
  BinUnitType,
  HarvestSettings,
  LabelSize,
  PickingJob,
  StartBinsInput,
} from '../types/harvest';

// ============================================================================
// HARVEST SERVICE — bins, /api/harvest.
// Starting and finishing bins needs only to be on the job (pickers have no
// seat); the shed, lists and labels need a farm_jobs supervisor/creator/admin.
// ============================================================================

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function download(url: string, filename: string) {
  const blob = await (apiService.get(url, { responseType: 'blob' }) as unknown as Promise<Blob>);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

class HarvestService {
  async getSettings(): Promise<HarvestSettings> {
    return unwrap(await apiService.get<HarvestSettings>('/harvest/settings'));
  }

  async saveSettings(input: Partial<Pick<HarvestSettings, 'label_size' | 'season_start_month' | 'default_unit_type_id' | 'record_actual_windows' | 'share_harvest_dates'>>): Promise<HarvestSettings> {
    return unwrap(await apiService.put<HarvestSettings>('/harvest/settings', input));
  }

  async getUnitTypes(includeInactive = false): Promise<BinUnitType[]> {
    return unwrap(await apiService.get<BinUnitType[]>(`/harvest/unit-types${qs({ include_inactive: includeInactive || undefined })}`)) ?? [];
  }

  async saveUnitType(id: number | null, input: { name?: string; nominal_net_kg?: number | null; active?: boolean }): Promise<BinUnitType> {
    return unwrap(id == null
      ? await apiService.post<BinUnitType>('/harvest/unit-types', input)
      : await apiService.patch<BinUnitType>(`/harvest/unit-types/${id}`, input));
  }

  // ---- in the row ----------------------------------------------------------

  async myJobs(): Promise<PickingJob[]> {
    return unwrap(await apiService.get<PickingJob[]>('/harvest/my-jobs')) ?? [];
  }

  async myBins(date?: string): Promise<Bin[]> {
    return unwrap(await apiService.get<Bin[]>(`/harvest/bins/mine${qs({ date })}`)) ?? [];
  }

  async startBins(input: StartBinsInput): Promise<{ duplicate: boolean; bins: Bin[] }> {
    return unwrap(await apiService.post<{ duplicate: boolean; bins: Bin[] }>('/harvest/bins/start', input));
  }

  async finishBin(id: number, fillFraction: number, at?: string): Promise<Bin> {
    return unwrap(await apiService.post<Bin>(`/harvest/bins/${id}/finish`, { fill_fraction: fillFraction, at }));
  }

  // ---- shed ----------------------------------------------------------------

  async shedLookup(date: string, farmId?: number, q?: string): Promise<Bin[]> {
    return unwrap(await apiService.get<Bin[]>(`/harvest/bins/shed${qs({ date, farm_id: farmId, q })}`)) ?? [];
  }

  async receive(items: { id: number; fill_fraction?: number }[]): Promise<Bin[]> {
    return unwrap(await apiService.post<Bin[]>('/harvest/bins/receive', { items }));
  }

  async createAtShed(input: {
    cultivar_area_id: number; worker_ids: number[]; field_code?: string | null; fill_fraction?: number;
    unit_type_id?: number; date?: string; notes?: string | null;
  }): Promise<Bin> {
    return unwrap(await apiService.post<Bin>('/harvest/bins/at-shed', input));
  }

  async findByTag(code: string): Promise<Bin | null> {
    try {
      return unwrap(await apiService.get<Bin>(`/harvest/bins/by-tag/${encodeURIComponent(code.trim())}`));
    } catch (e) {
      if ((e as { status?: number })?.status === 404) return null;
      throw e;
    }
  }

  async downloadLabels(ids: number[], size?: LabelSize, format: 'pdf' | 'zpl' = 'pdf'): Promise<void> {
    await download(
      `/harvest/bins/labels${qs({ ids: ids.join(','), size, format })}`,
      format === 'zpl' ? 'bin-labels.zpl' : 'bin-labels.pdf',
    );
  }

  async listBins(filters: { from?: string; to?: string; farm_id?: number; job_id?: number; needs_review?: boolean; include_voided?: boolean } = {}): Promise<Bin[]> {
    return unwrap(await apiService.get<Bin[]>(`/harvest/bins${qs(filters)}`)) ?? [];
  }

  async updateBin(id: number, input: {
    cultivar_area_id?: number; row_number?: number | null; field_code?: string | null; fill_fraction?: number;
    worker_ids?: number[]; notes?: string | null; reason: string;
  }): Promise<Bin> {
    return unwrap(await apiService.patch<Bin>(`/harvest/bins/${id}`, input));
  }

  async voidBin(id: number, reason: string): Promise<Bin> {
    return unwrap(await apiService.post<Bin>(`/harvest/bins/${id}/void`, { reason }));
  }

  // ---- loads ---------------------------------------------------------------

  async listLoads(filters: { from?: string; to?: string; status?: string } = {}): Promise<HarvestLoad[]> {
    return unwrap(await apiService.get<HarvestLoad[]>(`/harvest/loads${qs(filters)}`)) ?? [];
  }

  async getLoad(id: number): Promise<HarvestLoad> {
    return unwrap(await apiService.get<HarvestLoad>(`/harvest/loads/${id}`));
  }

  async createLoad(input: LoadInput): Promise<HarvestLoad> {
    return unwrap(await apiService.post<HarvestLoad>('/harvest/loads', input));
  }

  async updateLoad(id: number, input: LoadInput): Promise<HarvestLoad> {
    return unwrap(await apiService.patch<HarvestLoad>(`/harvest/loads/${id}`, input));
  }

  async addBinsToLoad(id: number, input: { tags?: string[]; bin_ids?: number[] }): Promise<{ results: AddBinResult[]; load: HarvestLoad }> {
    return unwrap(await apiService.post<{ results: AddBinResult[]; load: HarvestLoad }>(`/harvest/loads/${id}/bins`, input));
  }

  async removeBinFromLoad(id: number, binId: number): Promise<HarvestLoad> {
    return unwrap(await apiService.delete<HarvestLoad>(`/harvest/loads/${id}/bins/${binId}`));
  }

  async dispatchLoad(id: number): Promise<HarvestLoad> {
    return unwrap(await apiService.post<HarvestLoad>(`/harvest/loads/${id}/dispatch`));
  }

  async markDelivered(id: number): Promise<HarvestLoad> {
    return unwrap(await apiService.post<HarvestLoad>(`/harvest/loads/${id}/delivered`));
  }

  async voidLoad(id: number, reason: string): Promise<void> {
    unwrap(await apiService.post(`/harvest/loads/${id}/void`, { reason }));
  }

  async downloadDocket(load: Pick<HarvestLoad, 'id' | 'load_number'>): Promise<void> {
    await download(`/harvest/loads/${load.id}/docket`, `docket-load-${load.load_number}.pdf`);
  }

  // ---- packouts --------------------------------------------------------------

  async getGrades(includeInactive = false): Promise<PackoutGrade[]> {
    return unwrap(await apiService.get<PackoutGrade[]>(`/harvest/packout-grades${qs({ include_inactive: includeInactive || undefined })}`)) ?? [];
  }

  async saveGrade(id: number | null, input: { name?: string; kind?: string; active?: boolean }): Promise<PackoutGrade> {
    return unwrap(id == null
      ? await apiService.post<PackoutGrade>('/harvest/packout-grades', input)
      : await apiService.patch<PackoutGrade>(`/harvest/packout-grades/${id}`, input));
  }

  async listPackouts(filters: { from?: string; to?: string } = {}): Promise<Packout[]> {
    return unwrap(await apiService.get<Packout[]>(`/harvest/packouts${qs(filters)}`)) ?? [];
  }

  async getPackout(id: number): Promise<Packout> {
    return unwrap(await apiService.get<Packout>(`/harvest/packouts/${id}`));
  }

  async savePackout(id: number | null, input: PackoutInput): Promise<Packout> {
    return unwrap(id == null
      ? await apiService.post<Packout>('/harvest/packouts', input)
      : await apiService.put<Packout>(`/harvest/packouts/${id}`, input));
  }

  async voidPackout(id: number, reason: string): Promise<void> {
    unwrap(await apiService.post(`/harvest/packouts/${id}/void`, { reason }));
  }

  async packoutReport(from: string, to: string, groupBy: PackoutGroupKey): Promise<PackoutReport> {
    return unwrap(await apiService.get<PackoutReport>(`/harvest/reports/packout${qs({ from, to, group_by: groupBy })}`));
  }

  // --------------------------------------------------------------------------
  // Phase 5: actual harvest windows and yield history
  // --------------------------------------------------------------------------

  async getActualWindows(year?: number): Promise<ActualWindows> {
    return unwrap(await apiService.get<ActualWindows>(`/harvest/actual-windows${year ? `?year=${year}` : ''}`));
  }

  async syncActualWindows(year?: number): Promise<ActualWindows> {
    return unwrap(await apiService.post<ActualWindows>('/harvest/actual-windows/sync', year ? { year } : {}));
  }

  async applyActualWindow(cultivarAreaId: number, seasonYear: number): Promise<ActualWindowRow | null> {
    return unwrap(await apiService.post<ActualWindowRow | null>('/harvest/actual-windows/apply', { cultivar_area_id: cultivarAreaId, season_year: seasonYear }));
  }

  async getYieldHistory(farmId?: number | null, seasons = 5): Promise<YieldHistory> {
    return unwrap(await apiService.get<YieldHistory>(`/harvest/yield-history?seasons=${seasons}${farmId ? `&farm_id=${farmId}` : ''}`));
  }
}

const harvestService = new HarvestService();
export default harvestService;
